import {
  Box,
  FlowAccordionItem,
  Stack,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { boolean, option } from "fp-ts";
import { useState } from "react";
import { useFormatMessage } from "../../intl";
import { PageHeading } from "../../Common/PageHeading/PageHeading";
import { Option } from "fp-ts/Option";
import { BackButton } from "../../Common/BackButton/BackButton";
import { AdditionalIncome } from "./AdditionalIncome/AdditionalIncome";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { pipe } from "fp-ts/function";
import { emptyRework, Rework } from "../StandardLoanState";
import { MainContent } from "../../Common/MainContent";
import { FlowAccordion } from "../../Common/FlowAccordion/FlowAccordion";
import {
  ExpensesConfirmation,
  LfDecisionReceived,
} from "./ExpensesConfirmation/ExpensesConfirmation";

type Props = {
  onNext: (hasRepaidLoans: boolean) => unknown;
  onCounterOffer: (hasRepaidLoans: boolean) => unknown;
  onPending: () => unknown;
  onRejected: () => unknown;
  onBack: () => unknown;
  showAdditionalIncomeStep: boolean;
  reworkData: Rework;
};

export function ExpensesAndAdditionalIncome(props: Props) {
  const formatMessage = useFormatMessage();
  const [
    showAdditionalIncomeStep,
    setShowAdditionalIncomeStep,
  ] = useState<boolean>(props.showAdditionalIncomeStep);
  const [hasRepaidLoans, setHasRepaidLoans] = useState<boolean>(false);
  const [, setIsEditingAdditionalIncome] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<Option<number>>(
    option.some(
      props.showAdditionalIncomeStep &&
        option.isNone(props.reworkData.oldValues)
        ? 1
        : 0
    )
  );
  const [isHeaderReadonly, setIsHeaderReadonly] = useState(true);

  const onExpensesConfirmationNext = (
    needAdditionalIncomeStep: boolean,
    hasRepaidLoans: boolean,
    lfDecision: LfDecisionReceived
  ) => {
    if (lfDecision === "Rejected" && !needAdditionalIncomeStep) {
      props.onRejected();
    } else {
      pipe(
        needAdditionalIncomeStep,
        boolean.fold(
          () =>
            lfDecision === "Counteroffer"
              ? props.onCounterOffer(hasRepaidLoans)
              : props.onNext(hasRepaidLoans),
          () => {
            setHasRepaidLoans(hasRepaidLoans);
            setShowAdditionalIncomeStep(needAdditionalIncomeStep);
            setActiveIndex(option.some(1));
          }
        )
      );
    }
  };

  const expensesConfirmationStep: FlowAccordionItem = {
    title: formatMessage("StandardLoan.ExpensesConfirmation.Panel.title"),
    content: () => {
      return (
        <ExpensesConfirmation
          onExpensesConfirmationNext={onExpensesConfirmationNext}
          setIsHeaderReadonly={setIsHeaderReadonly}
        />
      );
    },
  };

  const additionalIncomeStep: FlowAccordionItem = {
    title: formatMessage("StandardLoan.AdditionalIncome.Panel.title"),
    content: () => (
      <AdditionalIncome
        onEditingStateChange={setIsEditingAdditionalIncome}
        onNext={() => props.onNext(hasRepaidLoans)}
        onCounterOffer={() => props.onCounterOffer(hasRepaidLoans)}
        onPending={props.onPending}
        onRejected={props.onRejected}
        reworkData={pipe(
          props.reworkData.oldValues,
          option.chain(oldValues => oldValues.additionalIncomeMap)
        )}
        reworkAll={props.reworkData.reworkAll}
      />
    ),
  };

  const items: NonEmptyArray<FlowAccordionItem> = pipe(
    props.reworkData.oldValues,
    option.fold(
      () => [
        expensesConfirmationStep,
        ...(showAdditionalIncomeStep ? [additionalIncomeStep] : []),
      ],
      () => [additionalIncomeStep]
    )
  );

  const isMobileLayout = useIsMobileLayout();
  const title = isHeaderReadonly
    ? "StandardLoan.ExpensesConfirmationAndAdditionalIncome.title"
    : "StandardLoan.ExpensesConfirmationAndAdditionalIncome.editTitle";

  return (
    <MainContent>
      <PageHeading
        title={formatMessage(title)}
        hideOnMobile
        contentRowType={"full"}
      />

      <Stack shrink column>
        <FlowAccordion
          items={items}
          value={activeIndex}
          onChange={setActiveIndex}
          forceDesktopStepsForMobile
          displayTopSteps
          compressedOnMobile
          displayNumbersOnTopMobileSteps
        />
        {props.reworkData === emptyRework && (
          <Box
            column={isMobileLayout}
            style={
              isMobileLayout ? { marginTop: "24px" } : { marginTop: "36px" }
            }
          >
            <BackButton action={props.onBack} />
          </Box>
        )}
      </Stack>
    </MainContent>
  );
}
